// This context provider returns wrappers around react-router-dom v6 exports.
// It's exposed as a context/provider so that consumers can share navigation/history/search params state.
// It should NOT be used directly, instead consumers should use @atom/hooks/useNavigation which has
// additional (optional) support for typed search params parsing

import React, {
  createContext,
  ForwardRefExoticComponent,
  PropsWithChildren,
  RefAttributes,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import {
  Link,
  LinkProps,
  NavigateFunction,
  Params,
  To,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import * as R from 'ramda';

export enum SearchParamType {
  NUMBER = 'NUMBER',
  ARRAY = 'ARRAY',
  BOOLEAN = 'BOOLEAN',
  STRING = 'STRING',
}

interface SearchParamsObject {
  [key: string]: string & boolean & number & string[];
}

export type NavigationContextValue = {
  searchParams: URLSearchParams;
  updateSearchParams: (
    payload: Object,
    replace?: boolean,
    state?: unknown,
  ) => void;
  backDestination: (defaultUrl?: string) => To;
  params: Readonly<Params<string>>;
  pathname: string;
  state: unknown;
  navigate: NavigateFunction;
  Link: ForwardRefExoticComponent<LinkProps & RefAttributes<HTMLAnchorElement>>;
};

const NOOP = () => {};

const initialContext: NavigationContextValue = {
  searchParams: new URLSearchParams(),
  updateSearchParams: NOOP,
  backDestination: () => '',
  params: {},
  pathname: '',
  state: null,
  navigate: NOOP,
  Link: null,
};

const NavigationContext = createContext<NavigationContextValue>(initialContext);

export const useNavigationContext = () => useContext(NavigationContext);

const NavigationProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tracks params across back-to-back writes so each merges onto the last,
  // not a stale render snapshot (which `setSearchParams(prev => ...)` would).
  // Rebase on external changes (navigate, <Link>, back/forward) so a later
  // merge can't resurrect params from the route we left — a mismatch here only
  // means external, since our own writes commit equal to the ref.
  const pendingParamsRef = useRef(searchParams);

  if (searchParams.toString() !== pendingParamsRef.current.toString()) {
    pendingParamsRef.current = searchParams;
  }

  // to delete a searchParam, pass it in as null or undefined.
  const updateSearchParams = useCallback(
    (payload: SearchParamsObject, replace = true, nextState?: unknown) => {
      const nextParams = R.keys(payload).reduce((acc, property) => {
        const value = payload[property];

        if (R.isNil(value)) {
          acc.delete(String(property));
        } else {
          acc.set(String(property), String(value));
        }

        return acc;
      }, new URLSearchParams(pendingParamsRef.current));

      pendingParamsRef.current = nextParams;
      setSearchParams(nextParams, { replace, state: nextState });
    },
    [setSearchParams],
  );

  const backDestination = useCallback((defaultUrl: string = '/'): To => {
    const canGoBack = window?.history?.length > 2;
    const destination: any = canGoBack ? -1 : defaultUrl;
    return destination;
  }, []);

  const value = useMemo<NavigationContextValue>(
    () => ({
      searchParams,
      updateSearchParams,
      backDestination,
      params,
      pathname,
      state,
      navigate,
      Link,
    }),
    [
      searchParams,
      updateSearchParams,
      backDestination,
      params,
      pathname,
      state,
      navigate,
    ],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationProvider;
